'use client';

import React from 'react';
import dynamic from 'next/dynamic';

// Dynamically import the existing ThirdPartyScripts (no SSR)
const ThirdPartyScripts = dynamic(() => import('./ThirdPartyScripts'), {
  ssr: false,
});

interface DeferredThirdPartyScriptsProps {
  nonce?: string;
}

// Defer rendering ThirdPartyScripts until the browser is idle or after a short timeout
export default function DeferredThirdPartyScripts({
  nonce,
}: DeferredThirdPartyScriptsProps) {
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    const schedule = () => setReady(true);

    // Prefer requestIdleCallback when available
    if ('requestIdleCallback' in window) {
      const w = window as Window & {
        requestIdleCallback: (
          cb: IdleRequestCallback,
          opts?: IdleRequestOptions,
        ) => number;
        cancelIdleCallback?: (id: number) => void;
      };
      const id = w.requestIdleCallback(() => schedule(), { timeout: 2000 });
      return () => {
        if (w.cancelIdleCallback) {
          w.cancelIdleCallback(id);
        }
      };
    } else {
      // Fallback: defer shortly after hydration
      const t = setTimeout(schedule, 1500);
      return () => clearTimeout(t);
    }
  }, []);

  if (!ready) {
    return null;
  }
  return <ThirdPartyScripts nonce={nonce} />;
}
